import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { PerspectiveShareButton } from '@/components/play/perspective-share-button'
import { useI18n } from '@/lib/i18n'
import {
  formatGameSettingDisplay,
  parseGameSettingToPlayerConfig,
} from '@/lib/play'
import { GameStatusData } from '@/lib/types.ts'
import { readSettingMode } from '@repo/sdk'
import { Users } from 'lucide-react'
import { type ReactNode, useMemo } from 'react'

interface GameStatusProps {
  data?: GameStatusData
  seatControl?: ReactNode
  takeoverCountdown?: ReactNode
  compact?: boolean
  sharedViewer?: boolean
}

export function isRandomGameStatusSetting(gameSetting: string | null | undefined): boolean {
  if (!gameSetting)
    return false
  const settingMode = readSettingMode(gameSetting)
  return settingMode.terminalSuffix === 'R' || settingMode.symbolicPlayerCount !== null
}

export function GameStatus({
  data,
  seatControl,
  takeoverCountdown,
  compact = false,
  sharedViewer = false,
}: GameStatusProps) {
  const { t } = useI18n()
  const players = useMemo(() => data?.players ?? [], [data])

  const status = data?.game?.status
  const gameSetting = data?.game?.gameSetting
  const playerCount = players.length
  const gameId = data?.game?.id
  const playerId = data?.humanPlayer?.id
  const isCompact = compact
  const canSharePerspective = !sharedViewer
    && !!gameId
    && !!playerId
    && data?.viewer?.ownedSeatIds.includes(playerId)
  const hasSeatControls
    = !sharedViewer && (!!seatControl || !!takeoverCountdown || canSharePerspective)
  const tournamentState = data?.game.tournamentState
  const tournamentNotice
    = data?.game.tournamentEditionId
      && (tournamentState === 'VOID'
        || tournamentState === 'FORFEITED_LOSS'
        || data?.game.retryLater)
  const statusLabel = tournamentNotice
    ? t(
        tournamentState === 'VOID'
          ? 'tournamentVoid.title'
          : tournamentState === 'FORFEITED_LOSS'
            ? 'tournamentParticipation.forfeitedLoss'
            : 'tournamentRecovery.tryLater',
      )
    : status ? t(`play.status.${status}`) : ''
  const statusVariant
    = status === 'in_progress'
      ? 'secondary'
      : status === 'completed'
        ? 'outline'
        : 'warning'

  const isRandomMode
    = isRandomGameStatusSetting(gameSetting)

  const formattedGameSetting = useMemo(() => {
    if (!gameSetting)
      return null

    if (isRandomMode)
      return null

    const parsed = parseGameSettingToPlayerConfig(gameSetting)
    if (parsed) {
      return formatGameSettingDisplay(parsed, t)
    }

    const translationKey = `gameSettings.${gameSetting}`
    const translated = t(translationKey)
    if (translated !== translationKey) {
      return translated
    }

    return gameSetting
  }, [gameSetting, t, isRandomMode])

  if (!data) {
    return null
  }

  return (
    <Card
      className={`flex-none border-border/50 bg-background/50 backdrop-blur-sm${isCompact ? ' py-2' : ' py-4 lg:py-6'}`}
      data-testid="game-status-panel"
    >
      <CardContent className={`grid px-3 sm:px-4${isCompact ? ' gap-2' : ' gap-3 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center lg:gap-4 lg:px-6'}`}>
        <div
          className={`flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground${isCompact ? '' : ' lg:flex-1'}`}
          data-testid="game-config"
        >
          <div className="flex shrink-0 items-center gap-2 whitespace-nowrap">
            <Users className="size-4" />
            <span>
              {t('play.players', { count: playerCount })}
              {isRandomMode && ` - ${t('game.g10RandomModeName')}`}
            </span>
          </div>
          {!isRandomMode && formattedGameSetting ? (
            <>
              <div className="h-4 w-px shrink-0 bg-border" />
              <span>{formattedGameSetting}</span>
            </>
          ) : null}
        </div>

        <div
          className={isCompact
            ? 'flex w-full min-w-0 flex-wrap items-center justify-between gap-2'
            : 'flex w-full min-w-0 flex-nowrap items-center justify-between gap-2 lg:w-auto lg:shrink-0'}
          data-testid="game-status-controls"
        >
          {hasSeatControls && (
            <div
              className={isCompact
                ? 'flex min-w-0 flex-1 flex-wrap items-center gap-2'
                : 'flex min-w-0 flex-1 items-center gap-2'}
              data-testid="game-status-seat-controls"
            >
              {!sharedViewer && takeoverCountdown && (
                <div className="min-w-0 flex-1">
                  {takeoverCountdown}
                </div>
              )}
              {!sharedViewer && seatControl}
              {canSharePerspective && gameId && playerId && (
                <PerspectiveShareButton
                  gameId={gameId}
                  playerId={playerId}
                  className={isCompact
                    ? 'size-9 shrink-0 gap-1 px-0 text-xs'
                    : 'size-11 shrink-0 gap-1 px-0 text-xs sm:h-8 sm:w-auto sm:px-2'}
                  labelClassName={isCompact ? 'hidden' : 'hidden sm:inline'}
                />
              )}
            </div>
          )}

          <div
            className={isCompact
              ? 'ml-auto flex min-w-0 flex-wrap items-center justify-end gap-2'
              : 'ml-auto flex min-w-0 shrink-0 flex-nowrap items-center justify-end gap-1 sm:gap-2'}
            data-testid="game-status-actions"
          >
            {status && (
              <Badge
                data-testid="turn-status-badge"
                variant={statusVariant}
                className={tournamentNotice
                  ? 'flex h-auto min-h-9 min-w-0 max-w-full items-center gap-1 whitespace-normal px-2 py-1 text-sm leading-5'
                  : isCompact
                    ? 'flex h-9 min-w-0 items-center gap-1 px-2 py-0 text-xs capitalize leading-none'
                    : 'flex h-11 min-w-0 items-center gap-1 px-2 py-0 text-xs capitalize leading-none sm:h-8 sm:w-auto'}
              >
                <span className={tournamentNotice ? 'break-words' : 'truncate'}>
                  {statusLabel}
                </span>
              </Badge>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
